import { Form } from '@ant-design/compatible';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Flex } from 'ui';
import moment from 'utils/cops-moment';
import { addPartRequest } from '../../../order/parts/containers/parts-actions';
import { miscellaniousCostRegex } from '../../../utils/app-configs';
import { antdLocaleData } from '../../../utils/i18n';
import HeaderInfo from './header-info';
import OrderInfo from './order-info';
import { UploadButton } from './upload-xml-button';

const Instruction = styled.p`
`;

const Buttons = styled(Flex)`
  > :not(:last-child) {
    margin-right: 20px;
  }
  justify-content: flex-end;
  margin-top: 30px;
`;

const Link = styled.p`
  color: #0098cc;
  text-decoration: underline;
  font-size: 16px;
  line-height: 32px;
  margin-right: auto !important;
  cursor: pointer;
`;

const Label = styled.p`
  color: rgba(100,116,124,0.85);
  font-weight: 400;
  font-size: 12px !important;
`;

const Wrapper = styled.p`
  font-size: 14px;
  max-height: 745px;
  overflow-y: auto;
`;

const Text = styled.span`
  font-size: 16px !important;
  font-weight: 400;
  ${p => p.marginLeft && `margin-left: ${p.marginLeft}px;`}
`;

const Item = styled.div`
  margin: 5px 5px 15px 5px;
`;

const StyledDatePicker = styled(DatePicker)`
  input {
    font-size: 16px !important;
    font-weight: 400;
    height: 34px !important;
  }
  height: 34px !important;
`;

const StyledInput = styled(Input)`
  ${p => p.mandatory && !p.value && `
    border: 1px solid red;
    height: 36px !important;
    &:focus {
      border: 1px solid red;
      height: 36px !important;
    }
  `}
`;

const StyledSelect = styled(Select)`
  ${p => p.mandatory && !p.value && `
    border: 1px solid red;
    height: 36px !important;
    &:focus {
      border: 1px solid red;
      height: 36px !important;
    }
  `}
  height: 34px;
  width: 100%;
  font-size: 16px !important;
  font-weight: 400;
  .ant-select-selector {
    height: 34px !important;
  }
`;

const ErrorText = styled.p`
  color: red;
  font-weight: 400;
`;

const StyledInputNumber = styled(InputNumber)`
  width: ${p => p.width};
  padding-left: ${p => p.isPrice ? '0px' : null} !important;
`;

const MoreDataInfo = ({
  instruction,
  headerInput,
  form,
  draftInputs,
  multiLineIds,
  model,
  intl,
  deleteDrafts,
  uploadAction,
  actionUri,
  workCenters,
  currencies,
  incoTerms,
  salesRepresentatives,
  closeMoreInfoNotification,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState(headerInput.currency);
  const [response, setResponse] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setResponse({
      multiLineActions: [...draftInputs],
      multiLineXmlHeaderInfo: { ...headerInput },
    });
  }, []);

  // Adjust the values of the response when the user changes them
  const getNewResponse = (object, value, id, isHeader, orderId) => {
    if (isHeader && id) {
      // In this case, the values are changes for all orders
      return {
        multiLineActions: [...object.multiLineActions],
        multiLineXmlHeaderInfo: { ...object.multiLineXmlHeaderInfo, [id]: value },
      };
    }
    if (id === 'parts') {
      return {
        multiLineActions: object.multiLineActions?.map((d) => {
          if (d.id === orderId) {
            return {
              ...d,
              partList: {
                ...d.partList,
                parts: value,
              },
            };
          }
          return d;
        }),
        multiLineXmlHeaderInfo: { ...object.multiLineXmlHeaderInfo },
      };
    }
    // In this case, the values must be changed for the correct orderId
    return {
      multiLineActions: object.multiLineActions?.map((d) => {
        if (id && d.id === orderId) {
          return {
            ...d,
            [id]: value,
          };
        }
        return d;
      }),
      multiLineXmlHeaderInfo: { ...object.multiLineXmlHeaderInfo },
    };
  };

  const handlePageSwitch = (page) => {
    if (currentPage > 1) {
      const formValues = form?.getFieldsValue();
      const orderId = response?.multiLineActions[currentPage - 2]?.id;

      const parts = response?.multiLineActions[currentPage - 2]?.partList?.parts?.map((part) => {
        if (formValues?.parts) {
          const formPart = formValues.parts[part?.id];
          if (formPart) {
            return {
              ...part,
              quantityOrdered: formPart.quantityOrdered,
              logisticFlow: formPart.logisticFlow,
              plant: formPart.plant,
            };
          }
          return part;
        }
        return part;
      });

      const newResponse = getNewResponse(response, parts || [], 'parts', false, orderId);
      // end of xml sending to MW
      if (page > currentPage && currentPage - 1 === multiLineIds?.length) {
        setIsLoading(true);
        uploadAction({ ...newResponse, uri: actionUri });
        return;
      }
      setResponse(newResponse);
    }
    setCurrentPage(page);
  };

  const handleChange = (e, isHeader, id, orderId) => {
    const newResponse = getNewResponse(response, e, id, isHeader, orderId);
    setResponse(newResponse);
  };

  const handleDropdownSelect = (
    e,
    isHeader,
    id,
    orderId,
    needsExtraEdit = false,
    extraId,
    extraValue,
  ) => {
    const newResponse = getNewResponse(response, e, id, isHeader, orderId);
    if (needsExtraEdit) {
      const editedResponse = getNewResponse(newResponse, extraValue, extraId, isHeader, orderId);
      setResponse(editedResponse);
    } else {
      setResponse(newResponse);
    }
  };

  const validationXMLField = (field, value) => {
    if (value) {
      // validation on Sales Price
      if (field === 'salesPrice') {
        if (value > 0) {
          return true;
        }
        return false;
      }
      // validation on order description's length
      if (field === 'orderDescription') {
        if (value.length <= 40) {
          return true;
        }
        return false;
      }
      // validation on Miscellanious cost
      if (field === 'miscellaniousCost') {
        const parsedValue = parseFloat(value);
        if (!parsedValue || miscellaniousCostRegex.test(value)) {
          return true;
        }
        return false;
      }
      // field is not empty
      return true;
    }
    // field can be empty
    if (field === 'miscellaniousCost') {
      return true;
    }
    // field is empty
    return false;
  };

  // Check if all mandatory fields are filled in
  const isValid = () => {
    // 1) check the items that has to be filled up before going to the next page
    if (response && Object.keys(response)?.length) {
      const sectionArray = [];
      const actions = response?.multiLineActions &&
        response?.multiLineActions[currentPage - 2];
      if (currentPage === 1 && response?.multiLineXmlHeaderInfo) {
        const mandatoryFields = model?.fields
          ?.filter(f => f.visible && f.mandatory)
          ?.map(m => m.id);
        // check the response header array, that contains all the data that is need to be filled in
        // before going to the next screen.
        // check if these field are mandatory and push them in the array to check them later
        // Do the same for for other screens
        Object.keys(response?.multiLineXmlHeaderInfo).forEach((key) => {
          mandatoryFields?.forEach((f) => {
            if (key === f) {
              sectionArray.push(f);
            }
          });
        });
      } else if (currentPage !== 1) {
        if (actions && Object.keys(actions).length) {
          const mandatoryFields = draftInputs[currentPage - 2]?.inputModel?.fields
            ?.filter(f => f.visible && f.mandatory)?.map(m => m.id);
          mandatoryFields?.forEach((f) => {
            Object.keys(actions).forEach((key) => {
              if (key === f) {
                sectionArray.push(f);
              }
              return null;
            });
          });
        }
        // check if there are actions open for the parts
        response?.multiLineActions[currentPage - 2]?.partList?.parts?.forEach((part) => {
          if (part?.isObsolete || part?.supersededBy.length > 0) {
            // because parts are in a table, we can't check the field (name)
            sectionArray.push(false);
          }
        });

        // add none mandatory fields that need to be checked
        if (draftInputs[currentPage - 2]?.inputModel?.fields
          ?.find(f => f.id === 'miscellaniousCost')?.editable) {
          sectionArray.push('miscellaniousCost');
        }
      }

      // 2) check if those items are indeed filled up
      const validArray = sectionArray?.map((f) => {
        if (currentPage === 1) {
          if (response?.multiLineXmlHeaderInfo && response?.multiLineXmlHeaderInfo[f]) {
            return validationXMLField(f, response?.multiLineXmlHeaderInfo[f]);
          }
        } else if (currentPage !== 1) {
          if (actions) {
            return validationXMLField(f, actions[f]);
          }
        }
        return false;
      });
      return validArray.every(v => v);
    }
    return true;
  };

  const getFieldContent = (id, field, object, value, isHeader) => {
    let content = null;
    if (!field?.editable) {
      if (field?.id === 'incoTerm') {
        const currentIncoTerm = incoTerms?.find(o => o?.incoterms === value);
        content =
          <Text>{`${currentIncoTerm?.description} (${currentIncoTerm?.incoterms})`}</Text>;
      } else if (field?.id === 'businessSubType') {
        const subType = object?.businessSubTypes?.find(o => o?.additionalParameter === value);
        content =
          <Text>{subType?.description}</Text>;
      } else {
        content = <Text>{value}</Text>;
      }
    } else {
      if (field.type === 'string') {
        content = <StyledInput
          value={value}
          onChange={e => handleChange(e.target.value, isHeader, id, object?.id)}
          mandatory={field.mandatory}
          status={id === 'orderDescription' && value.length > 40 && 'error'}
        />;
      }
      if (field.type === 'number') {
        content = <StyledInputNumber
          value={value}
          min={field.isPrice ? 1 : 0}
          controls={!field.isPrice}
          isPrice={field.isPrice}
          width={field.isPrice ? '195px' : '100px'}
          onChange={e => handleChange(e, isHeader, id, object?.id)}
        />;
      }
      if (field.type === 'date') {
        content = <StyledDatePicker
          value={value ? moment(value, 'YYYY-MM-DDTHH:mm:ssZ') : null}
          onChange={e =>
            handleChange(
              e ? moment(e, 'YYYY-MM-DDTHH:mm:ssZ').utc() : null,
              isHeader,
              id,
              object?.id,
            )
          }
          locale={antdLocaleData[localStorage.getItem('locale')]?.DatePicker}
          format='L'
          allowClear={false}
        />;
      }
      if (field.type === 'dropdown') {
        if (field.id === 'workcenterId') {
          content = <StyledSelect
            mandatory={field.mandatory}
            getPopupContainer={t => t.parentNode}
            value={value}
            showSearch={true}
            onSelect={(e, o) =>
              handleDropdownSelect(e, isHeader, id, object?.id, true, 'workcenterNumber', o.number)
            }
            filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            options={workCenters[object.serviceCenterId]?.map(o => ({
              value: o.id,
              label: o.name,
              number: o.number,
            }))}
          />;
        }
        if (field.id === 'currency') {
          content = <StyledSelect
            mandatory={field.mandatory}
            value={value}
            showSearch={true}
            onSelect={(e) => {
              setSelectedCurrency(e);
              handleDropdownSelect(e, isHeader, id, object?.id);
            }}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            options={currencies?.map(o => ({
              value: o.id,
              label: o.name,
            }))}
            getPopupContainer={t => t.parentNode}
          />;
        }
        if (field?.id === 'businessSubType' || field?.id === 'incoTerm') {
          const optionsList = field?.id === 'businessSubType' ?
            object?.businessSubTypes : incoTerms;
          content = <StyledSelect
            mandatory={field.mandatory}
            value={value}
            showSearch={true}
            onSelect={(e, o) => {
              handleDropdownSelect(e, isHeader, id, object?.id, true, field?.id, o.value);
            }}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            getPopupContainer={t => t.parentNode}
            options={
              optionsList?.map(o => ({
                value: field?.id === 'incoTerm' ? o.incoterms : o.additionalParameter,
                label: o.description,
              }))
            }
          />;
        }
        if (field.id === 'salesRepresentativeNumber') {
          content = <StyledSelect
            mandatory={field.mandatory}
            value={value}
            showSearch={true}
            getPopupContainer={t => t.parentNode}
            onSelect={(e, o) =>
              handleDropdownSelect(
                e,
                isHeader,
                id,
                object?.id,
                true,
                'salesRepresentative',
                o.label,
              )
            }
            filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            options={salesRepresentatives?.map(o => ({
              value: o.number,
              label: o.name,
            }))}
          />;
        }
      }
    }
    return content;
  };

  // need to wait to fix timing issues
  const getNewPart = (data, orderId) =>
    new Promise((resolve) => {
      dispatch(addPartRequest({
        orderId,
        materials: data?.map(({ materialNumber, vendor, quantityOrdered, quantity, vendorId }) =>
        ({
          materialNumber,
          vendorId: vendorId || vendor,
          quantity: quantity || quantityOrdered,
        })),
        callback: newParts => resolve(newParts),
      }));
    });

  const handlePartAction = async (data, index, orderId, action) => {
    if (action === 'addPart') {
      const newParts = await getNewPart(data, orderId);
      const oldParts = response?.multiLineActions[index]?.partList?.parts || [];
      const parts = [...oldParts, ...newParts.parts || []];

      const newResponse = getNewResponse(response, parts, 'parts', false, orderId);
      setResponse(newResponse);
    }
    if (action === 'removePart') {
      const parts =
        response?.multiLineActions[index].partList?.parts?.filter(o => o?.id !== data?.id) || [];

      const newResponse = getNewResponse(response, parts, 'parts', false, orderId);
      setResponse(newResponse);
    }
  };

  const handleAddOfSupersededPart = async (supersededPart, originalPart, index, orderId) => {
    supersededPart.autoReplacementInfo.isAutoReplaced = false;
    supersededPart.autoReplacementInfo.originalPartDescription =
      originalPart?.materialDescription || '';
    supersededPart.autoReplacementInfo.originalPartNumber =
      originalPart?.materialNumber || '';

    const oldParts = response?.multiLineActions[index]?.partList?.parts || [];
    const newParts = await getNewPart([supersededPart], orderId);

    let parts = [...oldParts, ...newParts.parts || []];
    if (originalPart?.supersededBy?.length === 0) {
      // remove part if there is no supersession anymore
      parts = parts.filter(o => o?.id !== originalPart?.id);
    }

    const newResponse = getNewResponse(response, parts || [], 'parts', false, orderId);
    setResponse(newResponse);
  };

  const handleRemoveSupersededPart = (supersededPartId, partId, orderId, index) => {
    const parts = response?.multiLineActions[index].partList?.parts || [];
    const editedPart = parts?.find(p => p?.id === partId);
    if (editedPart) {
      const newPart = editedPart?.supersededBy?.filter(s => s.id !== supersededPartId);
      editedPart.supersededBy = newPart;
      // remove part if there is no supersession anymore
      if (newPart?.length === 0) {
        handlePartAction(editedPart, index, orderId, 'removePart');
      } else {
        const newResponse = getNewResponse(response, parts, 'parts', false, orderId);
        setResponse(newResponse);
      }
    }
  };

  const renderFields = (id, isHeader, index) => {
    const field = isHeader ? model?.fields?.find(f => f.id === id) :
      draftInputs[index - 1]?.inputModel.fields?.find(f => f.id === id);
    const object = isHeader ? headerInput : draftInputs[index - 1];
    const value = isHeader ?
      response?.multiLineXmlHeaderInfo[id] :
      response?.multiLineActions?.find(a => a.id === object.id)[id];
    const content = getFieldContent(id, field, object, value, isHeader);
    if (!field || !field?.visible) {
      return null;
    }
    if (field?.id === 'partList') {
      return field?.visible;
    }
    return (
      <Item>
        {field.isPrice || field.id === 'labourTime' || field.id === 'travelTime' ?
          <Label>{`${field.label} (${field.isPrice ? selectedCurrency : 'h'})`}</Label>
          : <Label>{field.label}</Label>
        }
        {content}
        {field.mandatory && !value ?
          <ErrorText>
            {intl.formatMessage(
              { id: `fileUpload.REQUIRED_${isHeader ? 'ALL' : 'ONE'}` },
              { label: field.label },
            )}
          </ErrorText> :
          null
        }
        {field.id === 'orderDescription' && (value.length > 40) &&
          <ErrorText>
            {intl.formatMessage(
              { id: 'rules.DESCRIPTION_40' },
              { label: field.label },
            )}
          </ErrorText>
        }
        {field.id === 'miscellaniousCost' &&
          field.editable &&
          !validationXMLField(field.id, value) &&
          <ErrorText>
            {intl.formatMessage(
              { id: 'rules.MISCELLANEOUS_RULE' },
            )}
          </ErrorText>
        }
      </Item>
    );
  };

  return (
    <Wrapper>
      <Instruction>
        {instruction}
      </Instruction>
      {response && Object.keys(response).length && currentPage === 1 ?
        <HeaderInfo
          intl={intl}
          multiLineIds={multiLineIds || []}
          renderField={id => renderFields(id, true)}
        /> :
        null
      }
      {response && Object.keys(response).length && currentPage > 1 ?
        <OrderInfo
          intl={intl}
          form={form}
          multiLineIds={multiLineIds || []}
          index={currentPage - 1}
          renderField={id => renderFields(id, false, currentPage - 1)}
          serviceCenterId={draftInputs[currentPage - 2]?.serviceCenterId}
          parts={response?.multiLineActions[currentPage - 2]?.partList?.parts || []}
          orderId={response?.multiLineActions[currentPage - 2]?.id}
          handlePartAction={handlePartAction}
          handleRemoveSupersededPart={handleRemoveSupersededPart}
          handleAddOfSupersededPart={handleAddOfSupersededPart}
        /> :
        null
      }
      <Buttons>
        {currentPage > 1 ?
          <Link onClick={() => handlePageSwitch(currentPage - 1)}>
            {`< ${intl.formatMessage({ id: 'fileUpload.BACK' })}`}
          </Link> :
          null
        }
        <UploadButton
          onClick={() => {
            closeMoreInfoNotification();
            deleteDrafts({ ids: multiLineIds });
          }}
          disabled={isLoading}
        >
          {intl.formatMessage({ id: 'fileUpload.CANCEL_UPLOAD' })}
        </UploadButton>
        {currentPage < multiLineIds?.length + 1 ?
          <UploadButton
            primary={1}
            onClick={() => handlePageSwitch(currentPage + 1)}
            disabled={!isValid()}
          >
            {`${intl.formatMessage({ id: 'fileUpload.NEXT' })} >`}
          </UploadButton> :
          <UploadButton
            primary={1}
            onClick={
              () => {
                handlePageSwitch(currentPage + 1);
              }
            }
            disabled={!isValid()}
            loading={isLoading}
          >
            {intl.formatMessage({ id: 'fileUpload.CREATE_ORDERS' })}
          </UploadButton>
        }
      </Buttons>
    </Wrapper>
  );
};

MoreDataInfo.propTypes = {
  instruction: PropTypes.string,
  headerInput: PropTypes.object,
  multiLineIds: PropTypes.array,
  intl: PropTypes.object,
  model: PropTypes.object,
  draftInputs: PropTypes.object,
  deleteDrafts: PropTypes.func,
  uploadAction: PropTypes.func,
  actionUri: PropTypes.string,
  workCenters: PropTypes.object,
  currencies: PropTypes.array,
  incoTerms: PropTypes.array,
  salesRepresentatives: PropTypes.array,
  form: PropTypes.object,
  closeMoreInfoNotification: PropTypes.func,
};

export default Form.create()(MoreDataInfo);
